import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Shield,
  User as UserIcon,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Copy,
  Check,
  Key,
  Eye,
  EyeOff,
  Power,
  Ban,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import { userManagementAPI } from '../services/api/adminApi';
import type { UserDetail, UserRole, UserStatus, UserListParams, UserStats } from '../types/admin';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { UserFormDialog } from '../components/admin/UserFormDialog';
import { UserChatsView } from '../components/admin/UserChatsView';
import { toast } from 'sonner';
import { Modal } from '../components/ui/Modal';
import { formatDateWithYear } from '../utils/dateFormat';

type ViewMode = 'list' | 'chats';

const UserManagementPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedUserForChats, setSelectedUserForChats] = useState<UserDetail | null>(null);

  const [users, setUsers] = useState<UserDetail[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [sortBy, setSortBy] = useState<'created_at' | 'last_login' | 'full_name' | 'email'>(
    'created_at'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [userStats, setUserStats] = useState<UserStats | null>(null);

  // Dialog states
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [userFormMode, setUserFormMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<UserDetail | undefined>(undefined);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserDetail | null>(null);
  const [saving, setSaving] = useState(false);
  const [tempPasswordDialog, setTempPasswordDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<UserDetail | null>(null);

  // Fetch users and stats when filters change
  useEffect(() => {
    const fetchData = async () => {
      try {
        const params: UserListParams = {
          page,
          page_size: pageSize,
          search: searchQuery || undefined,
          role: roleFilter || undefined,
          status: statusFilter || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        };

        const [usersResult, statsResult] = await Promise.all([
          userManagementAPI.list(params),
          userManagementAPI.getStats(),
        ]);

        setUsers(usersResult.data.items);
        setTotal(usersResult.data.total);
        setUserStats(statsResult.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        toast.error(t('admin.loadDataFailed'));
      }
    };

    fetchData();
  }, [page, pageSize, searchQuery, roleFilter, statusFilter, sortBy, sortOrder]);

  const filteredUsers = users;

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleAddUser = () => {
    setSelectedUser(undefined);
    setUserFormMode('create');
    setUserFormOpen(true);
  };

  const handleEditUser = (user: UserDetail) => {
    setSelectedUser(user);
    setUserFormMode('edit');
    setUserFormOpen(true);
  };

  const handleViewChats = (user: UserDetail) => {
    setSelectedUserForChats(user);
    setViewMode('chats');
  };

  const handleDeleteUser = (user: UserDetail) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setSaving(true);
    try {
      await userManagementAPI.delete(userToDelete.id);
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      setSelectedUsers(prev => prev.filter(id => id !== userToDelete.id));
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      toast.success(t('admin.userDeleted'));
    } catch (_error) {
      toast.error(t('admin.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setUsers(prev => prev.filter(user => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);
      setBulkDeleteConfirmOpen(false);
      toast.success(t('admin.dialog.bulkDeleteSuccess', { count: selectedUsers.length }));
    } catch (_error) {
      toast.error(t('admin.dialog.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUser = async (userData: Partial<UserDetail>) => {
    setSaving(true);
    try {
      if (userFormMode === 'create') {
        // Call real API to create user
        const createData = {
          email: userData.email!,
          full_name: userData.full_name!,
          role: userData.role!,
          password: (userData as any).password,
        };
        const response = await userManagementAPI.create(createData);
        setUsers(prev => [response.data, ...prev]);
        toast.success(t('admin.createSuccess'));
      } else {
        // Call real API to update user (status is managed separately via activate/deactivate)
        const updateData = {
          full_name: userData.full_name!,
          role: userData.role!,
        };
        const response = await userManagementAPI.update(selectedUser!.id, updateData);
        setUsers(prev => prev.map(user => (user.id === selectedUser?.id ? response.data : user)));
        toast.success(t('admin.dialog.updateSuccess'));
      }
      setUserFormOpen(false);
    } catch (_error) {
      toast.error(t('admin.dialog.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      const updateData = {
        full_name: user.full_name,
        role: newRole as UserRole,
        status: user.status,
      };
      const response = await userManagementAPI.update(userId, updateData);
      setUsers(prev => prev.map(u => (u.id === userId ? response.data : u)));
      toast.success(t('admin.userUpdated'));
    } catch (_error) {
      toast.error(t('admin.saveFailed'));
    }
  };

  const handleUserCreated = (password: string) => {
    setTempPassword(password);
    setTempPasswordDialog(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_error) {
      toast.error(t('admin.copyPasswordFailed'));
    }
  };

  const handleCloseTempPasswordDialog = () => {
    setTempPasswordDialog(false);
    setTempPassword('');
    setCopied(false);
  };

  const handleResetPassword = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserToResetPassword(user);
      setResetPasswordConfirmOpen(true);
    }
  };

  const confirmResetPassword = async () => {
    if (!userToResetPassword) return;

    setSaving(true);
    try {
      const response = await userManagementAPI.resetPassword(userToResetPassword.id);
      setTempPassword(response.data.temporary_password);
      setTempPasswordDialog(true);
      setCopied(false);
      setResetPasswordConfirmOpen(false);
      setUserToResetPassword(null);
      toast.success(t('admin.dialog.passwordResetSuccess'));
    } catch (_error) {
      toast.error(t('admin.dialog.passwordResetError'));
    } finally {
      setSaving(false);
    }
  };

  const handleActivateUser = async (userId: number) => {
    try {
      const response = await userManagementAPI.activate(userId);
      setUsers(prev => prev.map(u => (u.id === userId ? response.data : u)));
      toast.success(t('admin.activateSuccess'));
    } catch (_error) {
      toast.error(t('admin.activateError'));
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    try {
      const response = await userManagementAPI.deactivate(userId);
      setUsers(prev => prev.map(u => (u.id === userId ? response.data : u)));
      toast.success(t('admin.deactivateSuccess'));
    } catch (_error) {
      toast.error(t('admin.deactivateError'));
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'user':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // Show chats view
  if (viewMode === 'chats' && selectedUserForChats) {
    return (
      <UserChatsView
        userId={selectedUserForChats.id}
        userName={selectedUserForChats.full_name}
        onBack={() => {
          setViewMode('list');
          setSelectedUserForChats(null);
        }}
      />
    );
  }

  if (!hasPermission('manage_users')) {
    return (
      <div className='flex-1 flex items-center justify-center'>
        <div className='text-center'>
          <Shield className='mx-auto h-12 w-12 text-gray-400' />
          <h2 className='mt-4 text-lg font-medium text-gray-900'>{t('admin.accessDenied')}</h2>
          <p className='mt-2 text-sm text-gray-500'>{t('admin.noPermission')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 bg-gray-50 overflow-hidden flex flex-col'>
      {/* Header */}
      <div className='bg-white border-b border-gray-200 px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4 flex-shrink-0'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
              {t('admin.userManagement')}
            </h1>
            <p className='text-gray-600 mt-1 text-sm sm:text-base'>{t('admin.subtitle')}</p>
          </div>
          <div className='flex flex-wrap items-center gap-2 sm:gap-4'>
            {selectedUsers.length > 0 && (
              <div className='flex items-center space-x-2 w-full sm:w-auto'>
                <span className='text-sm text-gray-600'>
                  {selectedUsers.length} {t('admin.usersSelected')}
                </span>
                <button
                  onClick={handleBulkDelete}
                  className='px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-shrink-0'
                >
                  {t('admin.deleteSelected')}
                </button>
              </div>
            )}
            <button
              onClick={handleAddUser}
              data-testid='create-user-button'
              className='flex items-center justify-center space-x-2 bg-ui-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto'
            >
              <Plus size={18} />
              <span>{t('admin.addUser')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:space-x-4 gap-3'>
          <div className='flex-1 relative w-full'>
            <Search
              className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'
              size={18}
            />
            <input
              type='text'
              data-testid='user-search-input'
              placeholder={t('admin.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base'
            />
          </div>
          <div className='flex flex-wrap gap-2'>
            <select
              data-testid='role-filter'
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as UserRole | '')}
              className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base'
            >
              <option value=''>{t('admin.dialog.allRoles')}</option>
              <option value='admin' data-testid='filter-role-admin'>
                {t('admin.roles.admin')}
              </option>
              <option value='user' data-testid='filter-role-user'>
                {t('admin.roles.user')}
              </option>
              <option value='viewer'>{t('admin.roles.viewer')}</option>
            </select>
            <select
              data-testid='status-filter'
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as UserStatus | '')}
              className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base'
            >
              <option value=''>{t('admin.dialog.allStatus')}</option>
              <option value='active' data-testid='filter-status-active'>
                {t('admin.active')}
              </option>
              <option value='inactive'>{t('admin.inactive')}</option>
              <option value='suspended'>{t('admin.suspended')}</option>
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base'
            >
              <option value='created_at'>{t('admin.dialog.sortCreated')}</option>
              <option value='last_login'>{t('admin.dialog.sortLastLogin')}</option>
              <option value='full_name'>{t('admin.dialog.sortName')}</option>
              <option value='email'>{t('admin.dialog.sortEmail')}</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className='px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base'
            >
              {sortOrder === 'asc'
                ? `↑ ${t('admin.dialog.ascending')}`
                : `↓ ${t('admin.dialog.descending')}`}
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className='px-4 sm:px-6 py-4 sm:py-6 flex-1 overflow-y-auto'>
        {/* Mobile Card View */}
        <div className='sm:hidden space-y-3 mb-4'>
          {filteredUsers.length === 0 ? (
            <div className='bg-white border border-gray-200 rounded-lg p-6 text-center'>
              <UserIcon className='mx-auto h-10 w-10 text-gray-400' />
              <h3 className='mt-2 text-sm font-medium text-gray-900'>{t('admin.noUsersFound')}</h3>
              <p className='mt-1 text-sm text-gray-500'>{t('admin.tryAdjustingSearch')}</p>
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                className='bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors'
              >
                <div className='flex items-start gap-3'>
                  <input
                    type='checkbox'
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    className='mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-3 mb-2'>
                      <div className='w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0'>
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.full_name}
                            className='w-10 h-10 rounded-full'
                          />
                        ) : (
                          <UserIcon size={20} className='text-gray-500' />
                        )}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <div className='text-sm font-medium text-gray-900 truncate'>
                          {user.full_name}
                        </div>
                        <div className='text-xs text-gray-500 truncate'>{user.email}</div>
                      </div>
                    </div>
                    <div className='flex flex-wrap items-center gap-2 mb-2'>
                      <select
                        value={user.role}
                        onChange={e => handleRoleChange(user.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border ${getRoleBadgeColor(user.role)}`}
                        disabled={user.id === 1}
                      >
                        <option value='viewer'>{t('admin.roles.viewer')}</option>
                        <option value='user'>{t('admin.roles.user')}</option>
                        <option value='admin'>{t('admin.roles.admin')}</option>
                      </select>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(user.status)}`}
                      >
                        {user.status}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <div className='text-xs text-gray-500'>
                        {t('admin.dialog.joined')}: {formatDateWithYear(user.created_at) || 'N/A'}
                      </div>
                      <div className='flex items-center gap-1'>
                        <button
                          onClick={() => handleViewChats(user)}
                          className='p-1.5 hover:bg-gray-100 rounded'
                          title={t('admin.chats.viewChats')}
                        >
                          <MessageSquare size={14} className='text-blue-500' />
                        </button>
                        {user.status === 'active' ? (
                          <button
                            onClick={() => handleDeactivateUser(user.id)}
                            className='p-1.5 hover:bg-gray-100 rounded'
                            disabled={user.id === 1}
                            title={t('admin.deactivate')}
                          >
                            <Ban
                              size={14}
                              className={user.id === 1 ? 'text-gray-300' : 'text-orange-500'}
                            />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateUser(user.id)}
                            className='p-1.5 hover:bg-gray-100 rounded'
                            title={t('admin.activate')}
                          >
                            <Power size={14} className='text-green-500' />
                          </button>
                        )}
                        <button
                          onClick={() => handleResetPassword(user.id)}
                          className='p-1.5 hover:bg-gray-100 rounded'
                          title={t('admin.resetPassword')}
                        >
                          <Key size={14} className='text-orange-500' />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className='p-1.5 hover:bg-gray-100 rounded'
                          title={t('admin.editUser')}
                        >
                          <Edit size={14} className='text-gray-500' />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className='p-1.5 hover:bg-gray-100 rounded'
                          disabled={user.id === 1}
                          title={t('admin.deleteUser')}
                        >
                          <Trash2
                            size={14}
                            className={user.id === 1 ? 'text-gray-300' : 'text-red-500'}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className='hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full' data-testid='user-table'>
              <thead className='bg-gray-50 border-b border-gray-200'>
                <tr>
                  <th className='px-3 sm:px-6 py-3 text-left'>
                    <input
                      type='checkbox'
                      checked={
                        selectedUsers.length === filteredUsers.length && filteredUsers.length > 0
                      }
                      onChange={handleSelectAll}
                      className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                    />
                  </th>
                  <th
                    className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    data-testid='header-name'
                  >
                    <span data-testid='header-email'>{t('admin.user')}</span>
                  </th>
                  <th
                    className='hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    data-testid='header-role'
                  >
                    {t('admin.role')}
                  </th>
                  <th
                    className='hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    data-testid='header-status'
                  >
                    {t('admin.status')}
                  </th>
                  <th
                    className='hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    data-testid='header-created'
                  >
                    {t('admin.created')}
                  </th>
                  <th className='hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('admin.lastLogin')}
                  </th>
                  <th className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='px-3 sm:px-6 py-8 sm:py-12 text-center'>
                      <UserIcon className='mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400' />
                      <h3 className='mt-2 text-sm font-medium text-gray-900'>
                        {t('admin.noUsersFound')}
                      </h3>
                      <p className='mt-1 text-sm text-gray-500'>{t('admin.tryAdjustingSearch')}</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.id} className='hover:bg-gray-50' data-testid='user-row'>
                      <td className='px-3 sm:px-6 py-3 sm:py-4'>
                        <input
                          type='checkbox'
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                        />
                      </td>
                      <td className='px-3 sm:px-6 py-3 sm:py-4'>
                        <div className='flex items-center'>
                          <div className='w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0'>
                            {user.avatar_url ? (
                              <img
                                src={user.avatar_url}
                                alt={user.full_name}
                                className='w-8 h-8 sm:w-10 sm:h-10 rounded-full'
                              />
                            ) : (
                              <UserIcon size={16} className='text-gray-500 sm:w-5 sm:h-5' />
                            )}
                          </div>
                          <div className='ml-2 sm:ml-4 min-w-0 flex-1'>
                            <div
                              className='text-sm font-medium text-gray-900 truncate'
                              data-testid='user-name'
                            >
                              {user.full_name}
                            </div>
                            <div
                              className='text-xs sm:text-sm text-gray-500 truncate'
                              data-testid='user-email'
                            >
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4'>
                        <span data-testid='user-role'>
                          <select
                            value={user.role}
                            onChange={e => handleRoleChange(user.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border ${getRoleBadgeColor(user.role)}`}
                            disabled={user.id === 1}
                          >
                            <option value='viewer'>{t('admin.roles.viewer')}</option>
                            <option value='user'>{t('admin.roles.user')}</option>
                            <option value='admin'>{t('admin.roles.admin')}</option>
                          </select>
                        </span>
                      </td>
                      <td className='hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4'>
                        <span
                          data-testid='user-status'
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(user.status)}`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className='hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-500'>
                        {formatDateWithYear(user.created_at) || 'N/A'}
                      </td>
                      <td className='hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-500'>
                        {formatDateWithYear(user.last_login) || 'N/A'}
                      </td>
                      <td className='px-3 sm:px-6 py-3 sm:py-4'>
                        <div className='flex items-center space-x-1 sm:space-x-2'>
                          <button
                            data-testid='user-actions-button'
                            onClick={() => handleViewChats(user)}
                            className='p-1 hover:bg-gray-100 rounded'
                            title={t('admin.chats.viewChats')}
                          >
                            <MessageSquare size={14} className='text-blue-500 sm:w-4 sm:h-4' />
                          </button>
                          {user.status === 'active' ? (
                            <button
                              data-testid='deactivate-user-button'
                              onClick={() => handleDeactivateUser(user.id)}
                              className='p-1 hover:bg-gray-100 rounded'
                              disabled={user.id === 1}
                              title={t('admin.deactivate')}
                            >
                              <Ban
                                size={14}
                                className={
                                  user.id === 1
                                    ? 'text-gray-300 sm:w-4 sm:h-4'
                                    : 'text-orange-500 sm:w-4 sm:h-4'
                                }
                              />
                            </button>
                          ) : (
                            <button
                              data-testid='activate-user-button'
                              onClick={() => handleActivateUser(user.id)}
                              className='p-1 hover:bg-gray-100 rounded'
                              title={t('admin.activate')}
                            >
                              <Power size={14} className='text-green-500 sm:w-4 sm:h-4' />
                            </button>
                          )}
                          <button
                            data-testid='reset-password-button'
                            onClick={() => handleResetPassword(user.id)}
                            className='p-1 hover:bg-gray-100 rounded'
                            title={t('admin.resetPassword')}
                          >
                            <Key size={14} className='text-orange-500 sm:w-4 sm:h-4' />
                          </button>
                          <button
                            data-testid='edit-user-button'
                            onClick={() => handleEditUser(user)}
                            className='p-1 hover:bg-gray-100 rounded'
                            title={t('admin.editUser')}
                          >
                            <Edit size={14} className='text-gray-500 sm:w-4 sm:h-4' />
                          </button>
                          <button
                            data-testid='delete-user-button'
                            onClick={() => handleDeleteUser(user)}
                            className='p-1 hover:bg-gray-100 rounded'
                            disabled={user.id === 1}
                            title={t('admin.deleteUser')}
                          >
                            <Trash2
                              size={14}
                              className={
                                user.id === 1
                                  ? 'text-gray-300 sm:w-4 sm:h-4'
                                  : 'text-red-500 sm:w-4 sm:h-4'
                              }
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4'
            data-testid='user-pagination'
          >
            <div className='text-sm text-gray-600'>
              <Trans
                i18nKey='admin.showing'
                values={{
                  start: Math.min((page - 1) * pageSize + 1, total),
                  end: Math.min(page * pageSize, total),
                  total,
                }}
                components={[<strong />, <strong />, <strong />]}
              />
            </div>
            <div className='flex items-center gap-2'>
              <button
                data-testid='pagination-prev'
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className='p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronLeft size={16} />
              </button>
              <span className='text-sm text-gray-600'>
                {t('admin.pageOf', { page, total: totalPages })}
              </span>
              <button
                data-testid='pagination-next'
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className='p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronRight size={16} />
              </button>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className='ml-4 px-3 py-2 border border-gray-300 rounded-lg text-sm'
              >
                <option value='5'>{t('admin.perPage', { count: 5 })}</option>
                <option value='10'>{t('admin.perPage', { count: 10 })}</option>
                <option value='20'>{t('admin.perPage', { count: 20 })}</option>
                <option value='50'>{t('admin.perPage', { count: 50 })}</option>
              </select>
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className='mt-4 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4'>
          <div className='bg-white p-3 sm:p-6 border border-gray-200 rounded-lg'>
            <div className='flex items-center'>
              <div className='w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                <UserIcon size={16} className='text-blue-600' />
              </div>
              <div className='ml-3 sm:ml-4 min-w-0 flex-1'>
                <div className='text-xl sm:text-2xl font-bold text-gray-900 truncate'>
                  {userStats?.total_users || 0}
                </div>
                <div className='text-xs sm:text-sm text-gray-500 truncate'>
                  {t('admin.totalUsers')}
                </div>
              </div>
            </div>
          </div>
          <div className='bg-white p-3 sm:p-6 border border-gray-200 rounded-lg'>
            <div className='flex items-center'>
              <div className='w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Shield size={16} className='text-green-600' />
              </div>
              <div className='ml-3 sm:ml-4 min-w-0 flex-1'>
                <div className='text-xl sm:text-2xl font-bold text-gray-900 truncate'>
                  {userStats?.admin_count || 0}
                </div>
                <div className='text-xs sm:text-sm text-gray-500 truncate'>{t('admin.admins')}</div>
              </div>
            </div>
          </div>
          <div className='bg-white p-3 sm:p-6 border border-gray-200 rounded-lg'>
            <div className='flex items-center'>
              <div className='w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                <UserIcon size={16} className='text-purple-600' />
              </div>
              <div className='ml-3 sm:ml-4 min-w-0 flex-1'>
                <div className='text-xl sm:text-2xl font-bold text-gray-900 truncate'>
                  {(userStats?.total_users || 0) - (userStats?.admin_count || 0)}
                </div>
                <div className='text-xs sm:text-sm text-gray-500 truncate'>
                  {t('navigation.users')}
                </div>
              </div>
            </div>
          </div>
          <div className='bg-white p-3 sm:p-6 border border-gray-200 rounded-lg'>
            <div className='flex items-center'>
              <div className='w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Calendar size={16} className='text-gray-600' />
              </div>
              <div className='ml-3 sm:ml-4 min-w-0 flex-1'>
                <div className='text-xl sm:text-2xl font-bold text-gray-900 truncate'>
                  {userStats?.active_users || 0}
                </div>
                <div className='text-xs sm:text-sm text-gray-500 truncate'>
                  {t('admin.activeUsers')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Form Dialog */}
      <UserFormDialog
        open={userFormOpen}
        onClose={() => setUserFormOpen(false)}
        onSave={handleSaveUser}
        onUserCreated={handleUserCreated}
        user={selectedUser}
        mode={userFormMode}
      />

      {/* Reset Password Confirmation Dialog */}
      <ConfirmDialog
        data-testid='reset-password-confirmation-dialog'
        open={resetPasswordConfirmOpen}
        onClose={() => {
          setResetPasswordConfirmOpen(false);
          setUserToResetPassword(null);
        }}
        onConfirm={confirmResetPassword}
        title={t('admin.dialog.resetPasswordConfirmTitle')}
        message={t('admin.dialog.resetPasswordConfirmMessage', {
          name: userToResetPassword?.full_name || '',
        })}
        confirmText={t('admin.dialog.resetPasswordConfirm')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={saving}
      />

      {/* Temp Password Dialog */}
      <Modal
        open={tempPasswordDialog}
        onClose={handleCloseTempPasswordDialog}
        title={t('admin.dialog.tempPasswordTitle')}
        maxWidth='sm'
        footer={
          <div className='flex justify-end space-x-3'>
            <button
              type='button'
              onClick={handleCloseTempPasswordDialog}
              className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
            >
              {t('common.close')}
            </button>
          </div>
        }
      >
        <div className='space-y-4'>
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <div className='flex items-start space-x-3'>
              <Key className='text-blue-600 mt-0.5 flex-shrink-0' size={20} />
              <div className='flex-1'>
                <p className='text-sm font-medium text-blue-900'>
                  {t('admin.dialog.tempPasswordGenerated')}
                </p>
                <p className='text-xs text-blue-700 mt-1'>
                  {t('admin.dialog.tempPasswordWarning')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              {t('admin.dialog.tempPasswordLabel')}
            </label>
            <div className='flex space-x-2'>
              <div className='flex-1 relative'>
                <input
                  type={showTempPassword ? 'text' : 'password'}
                  value={tempPassword}
                  readOnly
                  className='w-full px-3 py-2 pr-10 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm text-gray-700'
                />
                <button
                  type='button'
                  onClick={() => setShowTempPassword(!showTempPassword)}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
                >
                  {showTempPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button
                type='button'
                onClick={handleCopyPassword}
                className='px-3 py-2 bg-ui-primary text-white rounded-lg hover:bg-ui-accent transition-colors flex items-center space-x-2'
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                <span>{copied ? t('common.copied') : t('common.copy')}</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        data-testid='delete-confirmation-dialog'
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title={t('admin.dialog.deleteConfirmTitle')}
        message={t('admin.dialog.deleteConfirmMessage', { name: userToDelete?.full_name || '' })}
        confirmText={t('admin.dialog.deleteConfirm')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={saving}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDelete}
        title={t('admin.dialog.bulkDeleteConfirmTitle')}
        message={t('admin.dialog.bulkDeleteConfirmMessage', { count: selectedUsers.length })}
        confirmText={t('admin.dialog.deleteConfirm')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={saving}
      />
    </div>
  );
};

export default UserManagementPage;
